import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbCopy, TbLink, TbLinkOff, TbPlus, TbTrash } from "react-icons/tb";
import { CircularProgress, Dialog, IconButton } from "@mui/material";
import { MdClose } from "react-icons/md";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { useAccountData } from "../../../../Hooks";
import { useActiveIbAccount } from "../../../../Hooks/useActiveIbAccount";
import { usePixKeys } from "../../../../Hooks/usePixKeys";
import { useToast } from "../../../../Hooks/toast";
import { useAuth } from "../../../../Contexts/AuthContext";
import { fetchOperatingAccounts } from "../../../../Services/operatingAccountsApi";
import { filterVirtualAccountsByActiveEntity } from "../../VirtualAccounts/virtualAccountsEntityScope";
import { PixKeyCreateModal } from "./PixKeyCreateModal";
import { PixKeyDeleteModal } from "./PixKeyDeleteModal";
import { PixKeySummary } from "./PixKeySummary";
import { PIX_KEYS_LIMIT } from "./constants";

const EXIT_RAIL_TYPE_TOKENS = new Set([
  "VIRTUAL",
  "WALLET",
  "OTC",
  "OTC_VIRTUAL",
  "SEGREGATION_WALLET",
]);

const EXIT_RAIL_ROLE_TOKENS = new Set([
  "otc_virtual",
  "obr_virtual",
  "segregation_wallet",
  "wallet",
  "otc",
]);

const isVirtualWalletTarget = (account) => {
  if (!account) return false;
  const rawType = String(account.raw?.account_type || account.displayType || "")
    .trim()
    .toUpperCase();
  const role = String(account.role || account.raw?.role || "")
    .trim()
    .toLowerCase();
  if (EXIT_RAIL_TYPE_TOKENS.has(rawType) || EXIT_RAIL_ROLE_TOKENS.has(role)) {
    return true;
  }
  return Boolean(account.isVirtual || account.raw?.is_virtual);
};

const PixKeyCard = ({
  item,
  onCopy,
  onDelete,
  onLink,
  onUnlink,
  canDelete,
  canLink,
  isVirtualAccount,
  linkingBusy,
}) => {
  const linkedBlocksDelete = Boolean(item?.linked && !item?.availableToLink);
  const showDelete = canDelete && !linkedBlocksDelete;
  const showUnlink = Boolean(canLink && item?.linked && !item?.availableToLink);
  // Matriz: link unowned keys to a WALLET. Virtual: one-click link available matriz keys here.
  const showLink = Boolean(
    canLink
      && (
        (!isVirtualAccount && !item?.linked && !item?.availableToLink)
        || (isVirtualAccount && item?.availableToLink)
      ),
  );

  return (
    <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <PixKeySummary item={item} className="min-w-0 flex-1" />

      <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
        <button
          type="button"
          className="btn-outline !h-9 !min-h-9 !px-3"
          onClick={() => onCopy(item)}
          aria-label={intl.get("pix.keys.copy") || "Copiar chave"}
        >
          <TbCopy className="h-4 w-4" />
          {intl.get("pix.keys.copy") || "Copiar"}
        </button>
        {showLink ? (
          <button
            type="button"
            className="btn-outline !h-9 !min-h-9 !px-3"
            onClick={() => onLink(item)}
            disabled={linkingBusy}
            title={
              isVirtualAccount
                ? (intl.get("pix.keys.linkHereTitle") || "Vincular esta chave a esta conta virtual")
                : (intl.get("pix.keys.linkTitle") || "Vincular a conta virtual")
            }
          >
            <TbLink className="h-4 w-4" />
            {intl.get("pix.keys.link") || "Vincular"}
          </button>
        ) : null}
        {showUnlink ? (
          <button
            type="button"
            className="btn-outline !h-9 !min-h-9 !px-3"
            onClick={() => onUnlink(item)}
            disabled={linkingBusy}
            title={intl.get("pix.keys.unlink") || "Desvincular"}
          >
            <TbLinkOff className="h-4 w-4" />
            {intl.get("pix.keys.unlink") || "Desvincular"}
          </button>
        ) : null}
        {showDelete ? (
          <button
            type="button"
            className="inline-flex h-9 min-h-9 items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 text-xs font-semibold text-red-600 transition hover:bg-red-50"
            onClick={() => onDelete(item)}
            aria-label={intl.get("pix.keys.delete") || "Excluir chave"}
          >
            <TbTrash className="h-4 w-4" />
            {intl.get("pix.keys.delete") || "Excluir"}
          </button>
        ) : null}
      </div>
    </div>
  );
};

const PixKeysSkeleton = () => (
  <div className="space-y-3 p-4">
    {[1, 2, 3].map((item) => (
      <div key={item} className="flex animate-pulse items-center gap-3 rounded-xl border border-gray-100 p-4">
        <div className="h-11 w-11 rounded-xl bg-gray-100" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-24 rounded bg-gray-100" />
          <div className="h-4 w-48 rounded bg-gray-100" />
          <div className="h-3 w-32 rounded bg-gray-100" />
        </div>
      </div>
    ))}
  </div>
);

const PixKeyLinkModal = ({
  open,
  keyItem,
  targets,
  loadingTargets,
  selectedTargetId,
  onSelectTarget,
  onClose,
  onConfirm,
  submitting,
}) => {
  if (!open || !keyItem) return null;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        className:
          "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-md",
      }}
    >
      <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div>
          <h2 className="text-base font-bold text-brand-dark">
            {intl.get("pix.keys.linkTitle") || "Vincular a conta virtual"}
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            {intl.get("pix.keys.linkHint")
              || "Escolha a conta virtual (WALLET). Enquanto vinculada, o crédito Pix vai para o destino."}
          </p>
        </div>
        <IconButton size="small" onClick={onClose} disabled={submitting} aria-label="Fechar">
          <MdClose />
        </IconButton>
      </div>

      <div className="space-y-3 px-4 py-4">
        <p className="truncate font-mono text-xs text-gray-600" title={keyItem.value}>
          {keyItem.value}
        </p>
        <label className="block space-y-1">
          <span className="text-xs font-medium text-gray-600">
            {intl.get("pix.keys.linkTarget") || "Conta virtual"}
          </span>
          <select
            className="input w-full"
            value={selectedTargetId}
            onChange={(event) => onSelectTarget(event.target.value)}
            disabled={submitting || loadingTargets}
          >
            <option value="">
              {loadingTargets
                ? (intl.get("pix.keys.linkTargetsLoading") || "Carregando…")
                : (intl.get("pix.keys.linkTargetPlaceholder") || "Selecione a WALLET")}
            </option>
            {targets.map((target) => (
              <option key={target.id} value={target.id}>
                {target.label}
              </option>
            ))}
          </select>
        </label>
        {!loadingTargets && targets.length === 0 ? (
          <p className="text-xs text-gray-500">
            {intl.get("pix.keys.linkTargetsEmpty")
              || "Nenhuma conta virtual elegível. Crie uma WALLET em Contas virtuais."}
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
        <button type="button" className="btn-outline w-auto" onClick={onClose} disabled={submitting}>
          {intl.get("common.cancel") || "Cancelar"}
        </button>
        <button
          type="button"
          className="btn-primary w-auto"
          onClick={onConfirm}
          disabled={submitting || !selectedTargetId}
        >
          {intl.get("pix.keys.linkConfirm") || "Vincular"}
        </button>
      </div>
    </Dialog>
  );
};

export const PixKeys = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const {
    isExitRailVirtual,
    entityId: activeEntityId,
    account: activeAccount,
  } = useActiveIbAccount();
  const activeAccountId = activeAccount?.id || "";
  const canCreateKey = hasPermission("pixkeycreate") && !isExitRailVirtual;
  const canDeleteKey = hasPermission("pixkeydelete") && !isExitRailVirtual;
  const canLinkKey = hasPermission("pixkeycreate");
  const { state: accountData, handleGET: fetchAccountGET } = useAccountData();

  useEffect(() => {
    fetchAccountGET();
  }, []);
  const {
    keys,
    loading,
    submitting,
    canCreate,
    createKey,
    deleteKey,
    linkKey,
    unlinkKey,
  } = usePixKeys();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkTargets, setLinkTargets] = useState([]);
  const [linkTargetsLoading, setLinkTargetsLoading] = useState(false);
  const [selectedWalletId, setSelectedWalletId] = useState("");
  const allowCreate = canCreateKey && canCreate;

  const loadLinkTargets = useCallback(async () => {
    setLinkTargetsLoading(true);
    try {
      const next = await fetchOperatingAccounts();
      const list = Array.isArray(next) ? next : [];
      const scoped = activeEntityId
        ? filterVirtualAccountsByActiveEntity(list, activeEntityId, isVirtualWalletTarget)
        : list.filter(isVirtualWalletTarget);
      const options = scoped
        .filter((account) => String(account.id) !== String(activeAccountId || ""))
        .map((account) => ({
          id: account.id,
          label: `${account.label || account.id} (WALLET)`,
        }));
      setLinkTargets(options);
    } catch {
      setLinkTargets([]);
      toast.addToast({
        type: "error",
        title: intl.get("pix.keys.linkTargetsError") || "Não foi possível carregar contas virtuais.",
      });
    } finally {
      setLinkTargetsLoading(false);
    }
  }, [activeAccountId, activeEntityId, toast]);

  const handleCopy = async (item) => {
    try {
      await navigator.clipboard.writeText(item.value);
      toast.addToast({
        type: "success",
        title: "Chave copiada",
        description: "A chave Pix foi copiada para a área de transferência.",
      });
    } catch {
      toast.addToast({
        type: "error",
        title: "Não foi possível copiar",
        description: "Tente copiar manualmente a chave exibida.",
      });
    }
  };

  const handleCreate = async (payload) => createKey(payload);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const success = await deleteKey(deleteTarget.id);
    if (success) {
      setDeleteTarget(null);
    }
  };

  const handleStartLink = async (item) => {
    // On virtual: link matriz key onto this active WALLET in one step.
    if (isExitRailVirtual && item?.availableToLink) {
      const originId = item.originAccountId || "";
      if (!activeAccountId || !item.id) return;
      await linkKey(item.id, activeAccountId, originId || null);
      return;
    }
    setLinkTarget(item);
    setSelectedWalletId("");
    await loadLinkTargets();
  };

  const handleConfirmLink = async () => {
    if (!linkTarget?.id || !selectedWalletId) return;
    const success = await linkKey(linkTarget.id, selectedWalletId);
    if (success) {
      setLinkTarget(null);
      setSelectedWalletId("");
    }
  };

  const handleUnlink = async (item) => {
    if (!item?.id) return;
    const originId = isExitRailVirtual
      ? (item.originAccountId || null)
      : null;
    await unlinkKey(item.id, originId);
  };

  const originHint = useMemo(() => {
    if (isExitRailVirtual) return null;
    return (
      intl.get("pix.keys.originAccountLinkHint")
      || "Para colocar uma chave em uma conta virtual: clique em Vincular e selecione a WALLET."
    );
  }, [isExitRailVirtual]);

  return (
    <div className="w-full">
      <PageHeaderWithBack
        title="Minhas chaves Pix"
        subTitle={
          isExitRailVirtual
            ? intl.get("pix.keys.virtualSubtitle")
            : "Cadastre, copie e gerencie as chaves vinculadas à sua conta para receber pagamentos."
        }
        backTo="/pix"
      />

      {isExitRailVirtual ? (
        <p
          role="status"
          className="mb-4 rounded-xl border border-[#e7a73f]/35 bg-[#e7a73f]/10 px-4 py-3 text-sm text-[#7a5418] dark:border-brand-gold-mid/40 dark:bg-brand-gold-mid/10 dark:text-brand-gold-mid"
        >
          {intl.get("pix.keys.virtualLimitedBanner")}
        </p>
      ) : originHint ? (
        <p className="mb-4 rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 text-sm text-gray-600 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40 dark:text-gray-300">
          {originHint}
        </p>
      ) : null}

      {canCreateKey ? (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            className="btn-primary w-full sm:w-auto"
            onClick={() => setCreateOpen(true)}
            disabled={!allowCreate || loading}
          >
            <TbPlus className="h-4 w-4" />
            Cadastrar chave
          </button>
        </div>
      ) : null}

      <div className="app-card overflow-hidden">
        <div className="border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-bold text-brand-dark">
            {isExitRailVirtual
              ? intl.get("pix.keys.linkedOnlyTitle")
              : "Chaves registradas"}
          </p>
          <p className="text-xs text-gray-500">
            {isExitRailVirtual
              ? intl.get("pix.keys.linkedOnlyHint")
              : `Você pode cadastrar até ${PIX_KEYS_LIMIT} chaves Pix por conta, conforme regras do Banco Central.`}
          </p>
        </div>

        {loading ? (
          <PixKeysSkeleton />
        ) : keys.length === 0 ? (
          <div className="app-empty-state m-4">
            <p className="font-semibold text-brand-dark">
              {isExitRailVirtual
                ? intl.get("pix.keys.virtualEmptyTitle")
                : "Nenhuma chave cadastrada"}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {isExitRailVirtual
                ? intl.get("pix.keys.virtualEmptyHint")
                : canCreateKey
                  ? "Cadastre sua primeira chave Pix para começar a receber pagamentos."
                  : "Você tem permissão apenas para visualizar chaves Pix."}
            </p>
            {canCreateKey ? (
              <button type="button" className="btn-primary mt-4" onClick={() => setCreateOpen(true)}>
                <TbPlus className="h-4 w-4" />
                Cadastrar chave
              </button>
            ) : null}
          </div>
        ) : (
          keys.map((item) => (
            <PixKeyCard
              key={item.id}
              item={item}
              onCopy={handleCopy}
              onDelete={setDeleteTarget}
              onLink={handleStartLink}
              onUnlink={handleUnlink}
              canDelete={canDeleteKey}
              canLink={canLinkKey}
              isVirtualAccount={isExitRailVirtual}
              linkingBusy={submitting}
            />
          ))
        )}

        {submitting && (
          <div className="flex items-center justify-center gap-2 border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
            <CircularProgress size={14} />
            Processando...
          </div>
        )}
      </div>

      {canCreateKey ? (
        <PixKeyCreateModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreate}
          submitting={submitting}
          accountData={accountData}
        />
      ) : null}

      {canDeleteKey ? (
        <PixKeyDeleteModal
          open={Boolean(deleteTarget)}
          keyItem={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          submitting={submitting}
        />
      ) : null}

      <PixKeyLinkModal
        open={Boolean(linkTarget)}
        keyItem={linkTarget}
        targets={linkTargets}
        loadingTargets={linkTargetsLoading}
        selectedTargetId={selectedWalletId}
        onSelectTarget={setSelectedWalletId}
        onClose={() => {
          if (!submitting) {
            setLinkTarget(null);
            setSelectedWalletId("");
          }
        }}
        onConfirm={handleConfirmLink}
        submitting={submitting}
      />
    </div>
  );
};
